import React, { useState, useEffect, useRef } from 'react';
import api, { calendarAPI } from '../../lib/api';
import { cn } from '../../lib/utils';
import {
  Search,
  MapPin,
  ChevronDown,
  Check,
  Globe,
  Loader2,
  X
} from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';

const COUNTRY_FLAGS = {
  'Canada': '🇨🇦',
  'USA': '🇺🇸',
  'UK': '🇬🇧',
  'Australia': '🇦🇺',
};

const COUNTRY_COLORS = {
  'Canada': 'bg-red-50 text-red-700 border-red-200',
  'USA': 'bg-blue-50 text-blue-700 border-blue-200',
  'UK': 'bg-purple-50 text-purple-700 border-purple-200',
  'Australia': 'bg-green-50 text-green-700 border-green-200',
};

export const JurisdictionSelector = ({ 
  value, 
  onChange, 
  placeholder = "Select jurisdiction...",
  className,
  disabled = false,
  apiEndpoint = "calendar" // "calendar" or "firm" - determines which endpoint to use
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [jurisdictions, setJurisdictions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState(null);
  const inputRef = useRef(null);

  // Fetch jurisdictions with search/filter
  const fetchJurisdictions = async (searchTerm = '', country = null) => {
    setLoading(true);
    try {
      // Use the appropriate endpoint based on context
      const params = { search: searchTerm || null, country };
      const response = apiEndpoint === "firm" 
        ? await api.get('/jurisdictions', { params })
        : await calendarAPI.getJurisdictions(searchTerm || null, country);
      
      // Handle both response formats
      const data = response.data;
      setJurisdictions(data.jurisdictions || data || []);
      setCountries(data.countries || []);
    } catch (error) {
      console.error('Failed to fetch jurisdictions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchJurisdictions();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchJurisdictions(search, selectedCountry);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, selectedCountry]);

  // Set selected jurisdiction when value changes
  useEffect(() => {
    if (value && jurisdictions.length > 0) {
      const found = jurisdictions.find(j => j.id === value);
      setSelectedJurisdiction(found || null);
    } else if (!value) {
      setSelectedJurisdiction(null);
    }
  }, [value, jurisdictions]);

  const handleSelect = (jurisdiction) => {
    setSelectedJurisdiction(jurisdiction);
    onChange(jurisdiction.id);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedJurisdiction(null);
    onChange(null);
    setSearch('');
  };

  // Group jurisdictions by country
  const groupedJurisdictions = jurisdictions.reduce((acc, j) => {
    if (!acc[j.country]) acc[j.country] = [];
    acc[j.country].push(j);
    return acc;
  }, {});

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !selectedJurisdiction && "text-muted-foreground",
            className
          )}
          data-testid="jurisdiction-selector"
        >
          <div className="flex items-center gap-2 truncate">
            {selectedJurisdiction ? (
              <>
                <span>{COUNTRY_FLAGS[selectedJurisdiction.country] || '🌍'}</span>
                <span className="truncate">{selectedJurisdiction.name}</span>
                <Badge variant="outline" className="text-xs ml-1">
                  {selectedJurisdiction.country}
                </Badge>
              </>
            ) : (
              <>
                <MapPin className="h-4 w-4" />
                <span>{placeholder}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            {selectedJurisdiction && (
              <X 
                className="h-4 w-4 opacity-50 hover:opacity-100" 
                onClick={handleClear}
              />
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        {/* Search Header */}
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              ref={inputRef}
              placeholder="Search jurisdictions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4"
              data-testid="jurisdiction-search-input"
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
            )}
          </div>
          
          {/* Country Filter Pills */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            <Badge
              variant={selectedCountry === null ? "default" : "outline"}
              className="cursor-pointer hover:bg-slate-100"
              onClick={() => setSelectedCountry(null)}
            >
              <Globe className="h-3 w-3 mr-1" />
              All
            </Badge>
            {countries.map(country => (
              <Badge
                key={country}
                variant={selectedCountry === country ? "default" : "outline"}
                className={cn(
                  "cursor-pointer hover:opacity-80",
                  selectedCountry === country ? "" : COUNTRY_COLORS[country]
                )}
                onClick={() => setSelectedCountry(selectedCountry === country ? null : country)}
              >
                {COUNTRY_FLAGS[country]} {country}
              </Badge>
            ))}
          </div>
        </div>

        {/* Results */}
        <ScrollArea className="h-[300px]">
          {loading && jurisdictions.length === 0 ? (
            <div className="p-4 text-center text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading jurisdictions...
            </div>
          ) : jurisdictions.length === 0 ? (
            <div className="p-4 text-center text-slate-500">
              <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No jurisdictions found</p>
              <p className="text-xs mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="p-2">
              {Object.entries(groupedJurisdictions).map(([country, items]) => (
                <div key={country} className="mb-3">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    <span>{COUNTRY_FLAGS[country] || '🌍'}</span>
                    <span>{country}</span>
                    <span className="text-slate-400">({items.length})</span>
                  </div>
                  <div className="space-y-0.5">
                    {items.map(jurisdiction => (
                      <div
                        key={jurisdiction.id}
                        className={cn(
                          "flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-colors",
                          value === jurisdiction.id 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-slate-100"
                        )}
                        onClick={() => handleSelect(jurisdiction)}
                        data-testid={`jurisdiction-option-${jurisdiction.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 opacity-70" />
                          <div>
                            <p className="font-medium text-sm">{jurisdiction.name}</p>
                            <p className={cn(
                              "text-xs",
                              value === jurisdiction.id ? "opacity-80" : "text-slate-500"
                            )}>
                              {jurisdiction.region_type} • {jurisdiction.court_rules_count} court rules
                            </p>
                          </div>
                        </div>
                        {value === jurisdiction.id && (
                          <Check className="h-4 w-4" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="p-2 border-t bg-slate-50 text-xs text-slate-500 text-center">
          {jurisdictions.length} jurisdiction(s) available
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default JurisdictionSelector;
